import { PanelRight } from "lucide-react";

import {
  NOT_YOURS_HINT,
  useOpenThreadInSidebar,
} from "@/AgentThread/AgentThreadSidebar/useOpenThreadInSidebar";
import { RobotoTooltip } from "@/shared/components/RobotoTooltip";
import { type AgentThreadRecord } from "@/shared/domain/agentThread";

import styles from "./OpenThreadInSidebarButton.module.css";

interface OpenThreadInSidebarButtonProps {
  thread: AgentThreadRecord;
}

/**
 * Moves this thread into the AI chat sidebar and sends the page back to the
 * thread list.
 *
 * The counterpart to the sidebar's "Open in thread view" button, which hands
 * the conversation the other way. Both directions leave it in exactly one
 * pane; {@link useOpenThreadInSidebar} carries the reasoning and the move
 * itself.
 */
export function OpenThreadInSidebarButton({
  thread,
}: OpenThreadInSidebarButtonProps) {
  const { isOwnThread, openHere } = useOpenThreadInSidebar(thread);

  return (
    <RobotoTooltip
      title={isOwnThread ? "Open in chat sidebar" : NOT_YOURS_HINT}
    >
      {/* The span keeps the tooltip alive while the button is disabled — a
          disabled button fires no pointer events of its own, so the hover has
          to land on a wrapper. It is also the only hover target a test can
          aim at once the button is disabled, hence the testid. */}
      <span
        className={styles.tooltipTarget}
        data-testid="open-thread-in-sidebar"
      >
        <button
          type="button"
          className={styles.button}
          disabled={!isOwnThread}
          // The tooltip text lands on the wrapping span and changes with the
          // disabled state, so the button carries its own stable name.
          aria-label="Open in chat sidebar"
          onClick={openHere}
        >
          <PanelRight className={styles.icon} aria-hidden="true" />
        </button>
      </span>
    </RobotoTooltip>
  );
}
